import React, { useEffect, useMemo, useState } from 'react';
import { Loader, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUtensils, faScroll, faShekelSign, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { OrderView, RestoreTableRequest } from '../../../../services/orders/orders.service';
import { orderlinesService, type OrderlineView } from '../../../../services/orderlines/orderlines.service';
import { paymentsService, type PaymentlineView } from '../../../../services/payments/payments.service';
import { ReceiptTopBar } from '../receipt/topbar/ReceiptTopBar';
import { ReceiptScreen } from '../receipt/screen/ReceiptScreen';
import { GuestNameEditModal } from '../receipt/guest-name/GuestNameEditModal';
import { calculateOrderDiscount } from '../orders/shared/discount.utils';
import { formatMoney, roundMoney } from '../../../../utils/money';
import orderClasses from '../orders/section/order-detail.module.scss';

type Props = {
  tenantId: string;
  employeeSession: string;
  isManager: boolean;
  sourceOrder: OrderView;
  onBack: () => void;
  onPrintReceipt: () => void;
  onRestore: (request: RestoreTableRequest) => void;
  onSaveGuestName: (orderId: number, guestName: string | null) => Promise<boolean>;
};

// Restore mode reuses the REAL bill screen; only חשבון active; פעולות → שחזר שולחן.
const PosRestoreBillSectionComponent: React.FC<Props> = ({ tenantId, employeeSession, isManager, sourceOrder, onBack, onPrintReceipt, onRestore, onSaveGuestName }) => {
  const [orderlines, setOrderlines] = useState<OrderlineView[]>([]);
  const [paymentlines, setPaymentlines] = useState<PaymentlineView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guestName, setGuestName] = useState<string | null>(sourceOrder.guestName ?? null);
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [isSavingGuest, setIsSavingGuest] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError(null);
    Promise.all([
      orderlinesService.listForOrder(tenantId, sourceOrder.id, employeeSession, { includeCanceled: true }),
      paymentsService.listForOrder(tenantId, sourceOrder.id, employeeSession),
    ])
      .then(([lines, payments]) => {
        if (!active) return;
        setOrderlines(lines);
        setPaymentlines(payments);
      })
      .catch((loadError: unknown) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'טעינת ההזמנה נכשלה');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tenantId, sourceOrder.id, employeeSession]);

  const cartTotal = useMemo(
    () => orderlines
      .filter((line) => line.status === 'sent' || line.status === 'skipped')
      .reduce((sum, line) => sum + roundMoney(line.unitPrice * line.quantity - (line.discountInput ?? 0), line.roundAmounts ?? sourceOrder.roundAmounts ?? false), 0),
    [orderlines, sourceOrder.roundAmounts],
  );
  const discount = calculateOrderDiscount(cartTotal, sourceOrder.discountMode, sourceOrder.discountInput, sourceOrder.roundAmounts ?? false);

  const tableLabel = sourceOrder.tableNumber ?? sourceOrder.orderCode;
  const dinersCount = Math.max(1, sourceOrder.dinersCount || 1);
  const tableAverage = formatMoney(Number(sourceOrder.totalAmount ?? 0) / dinersCount, sourceOrder.roundAmounts ?? false);

  // The bill renders against the locally-edited guest name without mutating the original order.
  const draftOrder: OrderView = { ...sourceOrder, guestName };

  const handleRestore = () => {
    // No inline item edits in this view; the edited customer name rides along. Original untouched.
    onRestore({ sourceOrderId: sourceOrder.id, guestName });
  };

  return (
    <>
      <ReceiptTopBar
        guestName={guestName}
        tableLabel={tableLabel}
        isManager={isManager}
        isDiscountInFlight={false}
        onPrint={onPrintReceipt}
        onActions={handleRestore}
        onEditGuestName={() => setIsGuestModalOpen(true)}
        actionsLabel="שכפל שולחן"
        actionsIcon={faRotateLeft as IconProp}
      />

      <section className={orderClasses.screen}>
        {isLoading && (
          <div className={orderClasses.openOrderPlaceholder}>
            <Loader />
          </div>
        )}
        {!isLoading && error && <p className={orderClasses.openOrderPlaceholder}>{error}</p>}
        {!isLoading && !error && (
          <ReceiptScreen order={draftOrder} orderlines={orderlines} paymentlines={paymentlines} discount={discount} statusTag="שולחן סגור" />
        )}
      </section>

      {/* Read-only preview of the orders-screen bottom bar: only חשבון active, the rest disabled. */}
      <div className={orderClasses.bottomBar} data-testid="restore-bottom-bar">
        <UnstyledButton component="button" type="button" data-testid="restore-back-table" className={orderClasses.bottomItem} onClick={onBack} aria-label="חזרה להיסטוריית שולחן">
          <span className={orderClasses.tableBadge}>{tableLabel}</span>
          <span className={orderClasses.bottomLabel}>שולחן</span>
        </UnstyledButton>

        <UnstyledButton component="button" type="button" data-testid="restore-average" className={orderClasses.bottomItem} disabled aria-label="ממוצע שולחן">
          <span className={orderClasses.bottomNumber}>{tableAverage}</span>
          <span className={orderClasses.bottomLabel}>ממוצע</span>
        </UnstyledButton>

        <UnstyledButton component="button" type="button" data-testid="restore-menu" className={orderClasses.bottomItem} disabled aria-label="תפריט">
          <span className={orderClasses.bottomIcon}>
            <FontAwesomeIcon icon={faUtensils as IconProp} />
          </span>
          <span className={orderClasses.bottomLabel}>תפריט</span>
        </UnstyledButton>

        <UnstyledButton component="button" type="button" data-testid="restore-bill" className={`${orderClasses.bottomItem} ${orderClasses.bottomItemActive}`} disabled aria-label="חשבון">
          <span className={orderClasses.bottomIcon}>
            <FontAwesomeIcon icon={faScroll as IconProp} />
          </span>
          <span className={orderClasses.bottomLabel}>חשבון</span>
        </UnstyledButton>

        <UnstyledButton component="button" type="button" data-testid="restore-payment" className={orderClasses.bottomItem} disabled aria-label="תשלום">
          <span className={orderClasses.bottomIcon}>
            <FontAwesomeIcon icon={faShekelSign as IconProp} />
          </span>
          <span className={orderClasses.bottomLabel}>תשלום</span>
        </UnstyledButton>
      </div>

      <GuestNameEditModal
        order={draftOrder}
        opened={isGuestModalOpen}
        onClose={() => setIsGuestModalOpen(false)}
        onSave={async (_orderId, nextName) => {
          // Persist the rename onto the (closed) source order, then reflect it on the bill.
          setIsSavingGuest(true);
          try {
            const ok = await onSaveGuestName(sourceOrder.id, nextName);
            if (ok) {
              setGuestName(nextName);
            }
            return ok;
          } finally {
            setIsSavingGuest(false);
          }
        }}
        isSaving={isSavingGuest}
      />
    </>
  );
};

export const PosRestoreBillSection = React.memo(PosRestoreBillSectionComponent);
